import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

// Client-side column sorting for a grid.
//
// Pass the rows to sort and an `accessors` map of { columnKey: (row) => value }.
// Define `accessors` as a MODULE-LEVEL constant in the page (not inline) so it's
// stable across renders. Returns the sorted rows plus the current sort state and
// a toggle to wire into <SortTh>. Nulls always sort last; numbers compare
// numerically, strings case-insensitively (with natural numeric ordering).
export function useTableSort(rows, accessors) {
  const [sort, setSort] = useState({ key: null, dir: 'asc' });

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const sorted = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    if (!sort.key || !accessors[sort.key]) return list;
    const acc = accessors[sort.key];
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;   // nulls/blanks last, regardless of direction
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [rows, sort, accessors]);

  return { sorted, sort, toggleSort };
}

// A clickable, sortable table header cell with an ascending/descending indicator.
export function SortTh({ label, sortKey, sort, onSort, className = '' }) {
  const active = sort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 ${className}`}
      title={`Sort by ${label}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active
          ? (sort.dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)
          : <ChevronsUpDown className="h-3 w-3 text-gray-300" />}
      </span>
    </th>
  );
}
